import * as THREE from "three";
import debug from "debug";

/**
 * Manages visual feedback for object selection and collision detection.
 * @category Experience
 * @subcategory ObjectManagement
 */
class VisualFeedbackManager {
  /**
   * Creates an instance of VisualFeedbackManager.
   * @param {Object} experience - The experience object containing the scene and event hub.
   */
  constructor(experience) {
    this.eventHub = experience.eventHub;
    this.scene = experience.scene;
    this.log = debug("app:objectmanagement:collisiondetection");
    this.currentSelection = new Set();
    this.currentColliding = new Set();
    this.setupListeners();
  }

  cleanup() {
    this.eventHub.off("selectionUpdated", this.handleSelectionUpdated.bind(this));
    this.eventHub.off("selectedObjectsInquiryResponse", this.handleSelectionUpdated.bind(this));
    this.eventHub.off("collisionUpdated", this.handleCollisionUpdated.bind(this));

    // Revert colors for all selected and colliding objects
    this.currentSelection.forEach((object) => this.revertColor(object));
    this.currentColliding.forEach((object) => this.revertColor(object));

    // Clear sets
    this.currentSelection.clear();
    this.currentColliding.clear();

    console.log("VisualFeedbackManager cleanup complete: Event listeners removed, selection and collision data cleared.");
  }

  /**
   * Sets up event listeners for selection and collision updates.
   */
  setupListeners() {
    this.eventHub.on(
      "selectionUpdated",
      this.handleSelectionUpdated.bind(this)
    );
    this.eventHub.on(
      "selectedObjectsInquiryResponse",
      this.handleSelectionUpdated.bind(this)
    );
    this.eventHub.on(
      "collisionUpdated",
      this.handleCollisionUpdated.bind(this)
    );
  }

  /**
   * Handles updates to the list of colliding objects.
   * @param {Object} event - The event containing colliding objects.
   * @param {Object[]} event.collidingObjects - An array of objects that are colliding.
   */
  handleCollisionUpdated(event) {
    this.log(
      "Colliding objects old",
      this.currentColliding,
      "new colliding objects",
      event.collidingObjects
    );

    if (!Array.isArray(event.collidingObjects)) {
      return;
    }

    const newColliding = new Set(event.collidingObjects);

    if (this.areSetsEqual(newColliding, this.currentColliding)) return;

    this.currentColliding.forEach((object) => {
      if (!newColliding.has(object)) {
        this.revertColor(object);
      }
    });

    this.currentColliding.clear();

    newColliding.forEach((object) => {
      this.changeColor(object, 0xff2c2c); // Set to red
      this.currentColliding.add(object);
    });

    this.eventHub.emit("inquireSelectedObjects");
  }

  /**
   * Handles updates to the list of selected objects.
   * @param {Object} event - The event containing selected objects.
   * @param {Object[]} event.selectedObjects - An array of objects that are selected.
   */
  handleSelectionUpdated(event) {
    if (!Array.isArray(event.selectedObjects)) {
      return;
    }
    const newSelection = new Set(event.selectedObjects);

    this.currentSelection.forEach((object) => {
      if (!newSelection.has(object) && !this.currentColliding.has(object)) {
        this.revertColor(object);
      }
    });

    this.currentSelection.clear();

    newSelection.forEach((object) => {
      this.currentSelection.add(object);
      if (this.currentColliding.has(object)) {
        this.changeColor(object, 0xff2c2c); // Ensure collision color takes precedence
      } else {
        this.changeColor(object, 0xc6ee3d); // Set to green
      }
    });

    this.log("current selectedObjects", this.currentSelection);
    this.log("currentColliding", this.currentColliding);
  }

  /**
   * Changes the color of an object's bounding box to indicate its state.
   * @param {THREE.Object3D} object - The object whose color will be changed.
   * @param {number} color - The hex color to apply.
   */
  changeColor(object, color) {
    object.children.forEach((child) => {
      if (child.name === "bBox" && child.isMesh) {
        if (!child.material) {
          child.material = new THREE.MeshStandardMaterial();
        }
        if (!child.userData.originalColor) {
          child.userData.originalColor = child.material.color.getHex();
        }
        child.material.color.set(color);
        child.material.opacity = 0.35;
        this.log(child, "changeColor", child.material.color.getHexString());
      }
    });
  }

  /**
   * Reverts the color of an object's bounding box to its original state.
   * @param {THREE.Object3D} object - The object whose color will be reverted.
   */
  revertColor(object) {
    object.children.forEach((child) => {
      if (
        child.name === "bBox" &&
        child.isMesh &&
        child.userData.originalColor
      ) {
        this.log(`Reverting color of ${child.name} to original`);
        child.material.color.set(child.userData.originalColor);
        child.material.opacity = 0;
      }
    });
  }

  /**
   * Compares two sets for equality.
   * @param {Set} setA - The first set to compare.
   * @param {Set} setB - The second set to compare.
   * @returns {boolean} True if the sets are equal, false otherwise.
   */
  areSetsEqual(setA, setB) {
    if (setA.size !== setB.size) return false;
    for (const item of setA) {
      if (!setB.has(item)) return false;
    }
    return true;
  }
}

export default VisualFeedbackManager;
